/**
 * NewIntakeForm — capture-at-call intake form (PR4b), mirroring the operator's
 * intake tracker. Dropdown OPTIONS come from tenant config via
 * useIntakeOptions (never hardcoded — §3); a value is accepted even if it isn't
 * on the list (lists are operator guidance). Required: Caller, Client, Service,
 * Calling For, Contact Method, Referrer type (Amanda r1 + Slice 1). Referrer type
 * defaults to the config "not collected" value. Creates an intake at New Prospect.
 *
 * Referrer type is the one list with NO fallback: when the tenant's list is
 * empty the dialog refuses loudly (an in-dialog alert naming the list and who
 * sets it up) rather than inventing an option or falling back to free text,
 * because a made-up referrer quietly degrades the referral data.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useTenant } from '@/lib/auth';
import { useIntakeOptions, useCreateWorkItem, useAssignOptions } from '@/lib/workspace';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { TextInput } from '@/components/primitives/TextInput';
import { nameCase as toNameCase, type NameCaseKind } from '@/lib/valueCase';
import { TextAction } from '@/components/workspace/TextAction';
import { ReferralSourceField } from '@/components/workspace/ReferralSourceField';
import { useOtherableSelect } from './intakeFields';
import type { UpdateWorkItemInput } from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';
import { contactSentence } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

export interface NewIntakeFormProps {
  onClose: () => void;
}

function ListField({ label, value, onChange, options, required, nameCase }: {
  label: string; value: string; onChange: (v: string) => void; options: string[]; required?: boolean; nameCase?: NameCaseKind;
}): ReactNode {
  return (
    <label className="block text-sm text-ink mb-2">
      {label}{required && <span className="text-pepper"> *</span>}
      {options.length > 0 ? (
        <SelectField size="field" value={value} onChange={(e) => onChange(e.target.value)} required={required}
          className="w-full mt-1">
          <option value="">Select</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </SelectField>
      ) : (
        <TextInput value={value} onChange={(e) => onChange(e.target.value)} required={required} nameCase={nameCase}
          className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm" />
      )}
    </label>
  );
}

function TextField({ label, value, onChange, type = 'text', required, nameCase }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; nameCase?: NameCaseKind;
}): ReactNode {
  return (
    <label className="block text-sm text-ink mb-2">
      {label}{required && <span className="text-pepper"> *</span>}
      {/* The email box is an email box by its type; a name box names its kind. */}
      <TextInput type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} nameCase={nameCase}
        className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm" />
    </label>
  );
}

/** The form's date box: TextField's label, the house DateInput. */
function DateField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): ReactNode {
  return (
    <label className="block text-sm text-ink mb-2">
      {label}
      <DateInput size="compact" className="mt-1 w-full" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function NewIntakeForm({ onClose }: NewIntakeFormProps): ReactNode {
  const { tenantSlug } = useTenant();
  const contact = useSupportContact();
  const { lists, defaults, freeText, isLoading: optionsLoading } = useIntakeOptions(tenantSlug);
  const { units } = useAssignOptions(tenantSlug); // Slice 3b: the unit picker at intake
  const createIntake = useCreateWorkItem(tenantSlug);
  const [callerName, setCallerName] = useState('');
  const [clientName, setClientName] = useState('');
  const [serviceRequested, setServiceRequested] = useState('');
  const [relationship, setRelationship] = useState('');
  const [contactMethod, setContactMethod] = useState('');
  const [contactDate, setContactDate] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isPoa, setIsPoa] = useState(false);
  const [referralText, setReferralText] = useState('');
  // Referral source is the 0112 tagged union (Directory-linked org/individual OR free text,
  // num_nonnulls <= 1). The picker (reused from the edit drawer) keeps these mutually
  // exclusive; linkedName is the picked partner's name for display (the picker emits only ids).
  const [referralOrgId, setReferralOrgId] = useState<string | null>(null);
  const [referralIndividualId, setReferralIndividualId] = useState<string | null>(null);
  const [referralLinkedName, setReferralLinkedName] = useState<string | null>(null);
  const [unitId, setUnitId] = useState('');
  const [zip, setZip] = useState('');
  const [zipInvalid, setZipInvalid] = useState(false);
  const [referrerType, setReferrerType] = useState('');
  const [intakeCallBy, setIntakeCallBy] = useState<string | null>(null);

  // referrer_type is required with no blank option; pre-select the config default
  // (decoupled from list order — r1 Q3) once intake-options loads.
  useEffect(() => {
    if (!referrerType && defaults.referrer_type) setReferrerType(defaults.referrer_type);
  }, [defaults.referrer_type, referrerType]);

  const callBy = useOtherableSelect(intakeCallBy, lists.intake_call_by, freeText.intake_call_by, setIntakeCallBy);

  // Merge the picker's partial edits into local referral state, mirroring how the edit
  // drawer folds them into a PATCH. Clearing both ids (Unlink) drops the resolved name.
  const applyReferralEdit = (edits: UpdateWorkItemInput): void => {
    if ('referral_org_id' in edits) setReferralOrgId(edits.referral_org_id ?? null);
    if ('referral_individual_id' in edits) setReferralIndividualId(edits.referral_individual_id ?? null);
    if ('referral_text' in edits) setReferralText(edits.referral_text ?? '');
    if (edits.referral_org_id === null && edits.referral_individual_id === null) setReferralLinkedName(null);
  };

  // Three states for the referrer list: loading (nothing to pick yet, no banner so
  // a normal load never flashes the refusal), empty once loaded (the refusal), populated.
  const referrerListEmpty = lists.referrer_type.length === 0;
  const referrerListMissing = !optionsLoading && referrerListEmpty;

  const canSubmit = callerName.trim() && clientName.trim() && serviceRequested && relationship && contactMethod && referrerType;

  return (
    <Dialog title="New intake" size="lg" testId="new-intake-dialog" onClose={onClose}>
        {referrerListMissing && (
          <p className="text-xs text-pepper-deep mb-3" role="alert" data-testid="new-intake-referrer-missing">
            {contactSentence(
              "The referrer list isn't set up yet, so intakes can't be added here. Ask {contact_admin} to set it up, and note the referral by hand until then.",
              contact,
            )}
          </p>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // Silent by design: native required validation covers the text fields, and
            // when the referrer list is empty the alert above is the loud part.
            if (!canSubmit) return;
            // Five digits, or nothing: the ZIP is never required, but a
            // typo would place the family in the wrong county.
            const zipTrimmed = zip.trim();
            if (zipTrimmed !== '' && !/^\d{5}(-\d{4})?$/.test(zipTrimmed)) {
              setZipInvalid(true);
              return;
            }
            setZipInvalid(false);
            // The form submits on Enter, so the names and a typed
            // relationship are cased here as their boxes would on leaving.
            const client = toNameCase(clientName.trim(), 'person');
            const caller = toNameCase(callerName.trim(), 'person');
            const typedRelationship = lists.contact_relationship.length === 0;
            createIntake.mutate({
              kind: 'intake',
              title: client,
              prospect_name: client,
              contact_name: caller || null,
              service_requested: serviceRequested || null,
              contact_relationship: relationship
                ? typedRelationship ? toNameCase(relationship, 'relationship') : relationship
                : null,
              contact_method: contactMethod || null,
              contact_date: contactDate || null,
              contact_phone: phone.trim() || null,
              contact_email: email.trim() || null,
              contact_is_poa: isPoa,
              // Referral tagged union (null-siblings): a Directory link wins; else free text.
              // The picker keeps state exclusive, so at most one is ever non-null (0112 CHECK).
              referral_org_id: referralOrgId,
              referral_individual_id: referralIndividualId,
              referral_text: referralOrgId || referralIndividualId ? null : (referralText.trim() || null),
              referrer_type: referrerType || null,
              intake_call_by: intakeCallBy === null || lists.intake_call_by.includes(intakeCallBy)
                ? intakeCallBy
                : toNameCase(intakeCallBy, 'person'),
              unit_id: unitId || null,
              prospect_zip: zipTrimmed === '' ? null : zipTrimmed.slice(0, 5),
            }, { onSuccess: onClose });
          }}
        >
          <div className="grid grid-cols-2 gap-x-4">
            <TextField label="Caller name" value={callerName} onChange={setCallerName} required nameCase="person" />
            <TextField label="Client name" value={clientName} onChange={setClientName} required nameCase="person" />
            <ListField label="Service requested" value={serviceRequested} onChange={setServiceRequested} options={lists.service_requested} required />
            {/* Founder ruling 2026-07-25: "Calling for" invited INVERTED entry
                (the client's relationship to the caller, not the caller's to
                the client). ONE label across capture and edit — the drawer's
                IntakeDetailsForm carries the same string for the same column. */}
            <ListField label="Relationship to client" value={relationship} onChange={setRelationship} options={lists.contact_relationship} required nameCase="relationship" />
            <ListField label="Contact method" value={contactMethod} onChange={setContactMethod} options={lists.contact_method} required />
            <DateField label="Contact date" value={contactDate} onChange={setContactDate} />
            <TextField label="Phone" value={phone} onChange={setPhone} type="tel" />
            <TextField label="Email" value={email} onChange={setEmail} type="email" />
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            {/* referrer_type = the referral CATEGORY (required, no blank, pre-selected
                to the config default); referral_text = the specific source. Disabled
                while the list is loading or empty: there is nothing honest to pick. */}
            <label className="block text-sm text-ink mb-2">
              Referrer type<span className="text-pepper"> *</span>
              <SelectField size="field" value={referrerType} onChange={(e) => setReferrerType(e.target.value)} required data-testid="referrer-type"
                disabled={referrerListEmpty}
                className="w-full mt-1">
                {lists.referrer_type.map((o) => <option key={o} value={o}>{o}</option>)}
              </SelectField>
            </label>
            {/* Referral source — the SAME picker as the edit drawer (Directory search to
                link a provider/org, else free text). Optional at entry; never blocks. */}
            <div className="text-sm text-ink mb-2">
              <ReferralSourceField
                referralText={referralText || null}
                referralOrgId={referralOrgId}
                referralIndividualId={referralIndividualId}
                linkedName={referralLinkedName}
                onChange={applyReferralEdit}
                onLink={(row) => setReferralLinkedName(row.display_name)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            {/* Slice 3b: the geographic unit, set early (optional at intake). */}
            <label className="block text-sm text-ink mb-2">
              Unit
              <SelectField size="field" value={unitId} onChange={(e) => setUnitId(e.target.value)} data-testid="intake-unit"
                className="w-full mt-1">
                <option value="">Not yet</option>
                {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </SelectField>
            </label>
            {/* The prospective client's ZIP, when the caller has it: enough
                to place the family and suggest a unit and care manager at
                the Assign step. Never required; the address usually
                arrives with the paperwork. */}
            <div>
              <TextField label="ZIP code" value={zip} onChange={(v) => { setZip(v); setZipInvalid(false); }} />
              {zipInvalid && (
                <p className="text-xs text-pepper-deep -mt-1 mb-2" role="alert" data-testid="new-intake-zip-invalid">
                  A ZIP code is five digits.
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-4 items-end">
            <label className="block text-sm text-ink mb-2">
              Intake call by
              <SelectField size="field" value={callBy.selectValue} onChange={(e) => callBy.onSelectChange(e.target.value)} data-testid="intake-call-by"
                className="w-full mt-1">
                <option value="">Not yet</option>
                {lists.intake_call_by.map((o) => <option key={o} value={o}>{o}</option>)}
              </SelectField>
              {callBy.showOther && (
                <TextInput nameCase="person" defaultValue={callBy.initialOther} onChange={(e) => callBy.onTextChange(e.target.value)} data-testid="intake-call-by-other"
                  placeholder="Type a name" aria-label="Intake call by, specify"
                  className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm" />
              )}
            </label>
            <label className="flex items-center gap-2 text-sm text-ink mb-3.5">
              <input type="checkbox" checked={isPoa} onChange={(e) => setIsPoa(e.target.checked)} data-testid="intake-poa" />
              Contact is POA
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <TextAction onClick={onClose}>Cancel</TextAction>
            {/* Not disabled on invalid — native required validation + the
                onSubmit guard handle it (a disabled button is a SR/keyboard dead end). */}
            <Button size="sm" type="submit" disabled={createIntake.isPending} data-testid="new-intake-submit">
              {createIntake.isPending ? 'Creating…' : 'Create intake'}
            </Button>
          </div>
        </form>
    </Dialog>
  );
}
